import React, { useState, useEffect } from 'react';
import './VirtualPet.css';

interface PetState {
  name: string;
  level: number;
  experience: number;
  happiness: number;
  health: number;
  hunger: number;
  appearance: string;
  accessories: string[];
}

interface DailyCheckIn {
  date: string;
  consecutiveDays: number;
  totalDays: number;
  lastCheckIn: string;
}

interface ChatStats {
  todayMinutes: number;
  totalMinutes: number;
  longestStreak: number;
}

const VirtualPet: React.FC = () => {
  const [pet, setPet] = useState<PetState>({
    name: '小爱',
    level: 1,
    experience: 0,
    happiness: 80,
    health: 90,
    hunger: 60,
    appearance: 'default',
    accessories: []
  });

  const [checkIn, setCheckIn] = useState<DailyCheckIn>({
    date: '',
    consecutiveDays: 0,
    totalDays: 0,
    lastCheckIn: ''
  });

  const [chatStats, setChatStats] = useState<ChatStats>({
    todayMinutes: 0,
    totalMinutes: 0,
    longestStreak: 0
  });

  const [dailyTopic, setDailyTopic] = useState<string>('');
  const [showRewards, setShowRewards] = useState(false);
  const [availableAccessories, setAvailableAccessories] = useState([
    { id: 'hat1', name: '可爱帽子', unlocked: false, cost: 100 },
    { id: 'bow1', name: '蝴蝶结', unlocked: false, cost: 150 },
    { id: 'glasses1', name: '时尚眼镜', unlocked: false, cost: 200 },
    { id: 'scarf1', name: '温暖围巾', unlocked: false, cost: 250 }
  ]);

  // 加载数据
  useEffect(() => {
    const savedPet = localStorage.getItem('virtualPet');
    const savedCheckIn = localStorage.getItem('dailyCheckIn');
    const savedChatStats = localStorage.getItem('chatStats');
    const savedAccessories = localStorage.getItem('petAccessories');

    if (savedPet) setPet(JSON.parse(savedPet));
    if (savedCheckIn) setCheckIn(JSON.parse(savedCheckIn));
    if (savedChatStats) setChatStats(JSON.parse(savedChatStats));
    if (savedAccessories) setAvailableAccessories(JSON.parse(savedAccessories));

    generateDailyTopic();
  }, []);

  // 保存数据
  useEffect(() => {
    localStorage.setItem('virtualPet', JSON.stringify(pet));
  }, [pet]);

  useEffect(() => {
    localStorage.setItem('dailyCheckIn', JSON.stringify(checkIn));
  }, [checkIn]);

  useEffect(() => {
    localStorage.setItem('chatStats', JSON.stringify(chatStats));
  }, [chatStats]);

  useEffect(() => {
    localStorage.setItem('petAccessories', JSON.stringify(availableAccessories));
  }, [availableAccessories]);

  // 宠物类型选择
  const [petType, setPetType] = useState<string>('cat');
  const [petMood, setPetMood] = useState<string>('happy');

  // 宠物类型配置
  const petTypes = {
    cat: {
      name: '小猫咪',
      emoji: '🐱',
      colors: ['#ff9a9e', '#fecfef', '#fecfef']
    },
    dog: {
      name: '小狗狗', 
      emoji: '🐶',
      colors: ['#a8edea', '#fed6e3', '#fed6e3']
    },
    rabbit: {
      name: '小兔子',
      emoji: '🐰',
      colors: ['#ffecd2', '#fcb69f', '#fcb69f']
    },
    bear: {
      name: '小熊熊',
      emoji: '🐻',
      colors: ['#ff9a9e', '#fad0c4', '#fad0c4']
    }
  };

  // 生成每日话题（模拟LLM调用）
  const generateDailyTopic = async () => {
    const topics = [
      '今天想和对方分享什么有趣的事情呢？',
      '如果可以一起去任何地方旅行，你们最想去哪里？',
      '分享一个让你们都开心的小习惯吧！',
      '今天最想对对方说的一句话是什么？',
      '一起计划一个浪漫的约会吧！',
      '分享一个关于未来的小梦想～',
      '今天想一起做什么有意义的事情？',
      '聊聊你们第一次见面时的感受吧！',
      '如果有一天可以实现任何愿望，你们会许什么愿？',
      '分享一个只有你们两个人知道的小秘密！'
    ];
    
    const randomTopic = topics[Math.floor(Math.random() * topics.length)];
    setDailyTopic(randomTopic);
  };

  // 每日签到
  const handleDailyCheckIn = () => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
    
    if (checkIn.lastCheckIn === today) {
      alert('今天已经签到过了哦！');
      return;
    }

    let newConsecutiveDays = 1;
    if (checkIn.lastCheckIn === yesterday) {
      newConsecutiveDays = checkIn.consecutiveDays + 1;
    }

    const newCheckIn = {
      date: today,
      consecutiveDays: newConsecutiveDays,
      totalDays: checkIn.totalDays + 1,
      lastCheckIn: today
    };

    setCheckIn(newCheckIn);
    
    // 签到奖励
    const expReward = newConsecutiveDays * 10;
    const happinessReward = 20;
    
    setPet(prev => ({
      ...prev,
      experience: prev.experience + expReward,
      happiness: Math.min(100, prev.happiness + happinessReward)
    }));

    setShowRewards(true);
    setTimeout(() => setShowRewards(false), 3000);
  };

  // 喂食宠物
  const feedPet = () => {
    setPetMood('eating');
    setTimeout(() => setPetMood('happy'), 2000);
    
    setPet(prev => ({
      ...prev,
      hunger: Math.min(100, prev.hunger + 30),
      happiness: Math.min(100, prev.happiness + 10),
      experience: prev.experience + 5
    }));
  };

  // 和宠物玩耍
  const playWithPet = () => {
    setPetMood('excited');
    setTimeout(() => setPetMood('happy'), 2000);
    
    setPet(prev => ({
      ...prev,
      happiness: Math.min(100, prev.happiness + 25),
      health: Math.min(100, prev.health + 10),
      experience: prev.experience + 8
    }));
  };

  // 切换宠物类型
  const changePetType = (type: string) => {
    setPetType(type);
    setPetMood('happy');
  };

  // 解锁装扮
  const unlockAccessory = (accessoryId: string) => {
    const accessory = availableAccessories.find(acc => acc.id === accessoryId);
    if (!accessory || pet.experience < accessory.cost) {
      alert('经验值不足，无法解锁此装扮！');
      return;
    }

    setAvailableAccessories(prev => 
      prev.map(acc => 
        acc.id === accessoryId ? { ...acc, unlocked: true } : acc
      )
    );

    setPet(prev => ({
      ...prev,
      experience: prev.experience - accessory.cost,
      accessories: [...prev.accessories, accessoryId]
    }));
  };

  // 计算等级
  const calculateLevel = (exp: number) => {
    return Math.floor(exp / 100) + 1;
  };

  // 更新聊天时长（模拟）
  const updateChatTime = () => {
    setChatStats(prev => ({
      ...prev,
      todayMinutes: prev.todayMinutes + 1,
      totalMinutes: prev.totalMinutes + 1
    }));
  };

  return (
    <div className="virtual-pet-container">
      <div className="pet-header">
        <h2>🐾 我们的小灵宠</h2>
        <button className="check-in-btn" onClick={handleDailyCheckIn}>
          📅 每日签到
        </button>
      </div>

      {showRewards && (
        <div className="reward-popup">
          <div className="reward-content">
            <h3>🎉 签到成功！</h3>
            <p>连续签到 {checkIn.consecutiveDays} 天</p>
            <p>获得经验值 +{checkIn.consecutiveDays * 10}</p>
            <p>快乐值 +20</p>
          </div>
        </div>
      )}

      <div className="pet-main">
        <div className="pet-display">
          <div className="pet-type-selector">
            <h4>选择你的小伙伴：</h4>
            <div className="pet-type-buttons">
              {Object.entries(petTypes).map(([type, config]) => (
                <button
                  key={type}
                  className={`pet-type-btn ${petType === type ? 'active' : ''}`}
                  onClick={() => changePetType(type)}
                >
                  <span className="pet-emoji">{config.emoji}</span>
                  <span className="pet-name">{config.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="pet-avatar" onClick={() => {
            playWithPet();
            setPetMood('excited');
            setTimeout(() => setPetMood('happy'), 2000);
          }}>
            <div className={`pet-character-realistic ${petType} ${petMood}`}>
              {/* 宠物头部 */}
              <div className="pet-head">
                <div className="pet-ears">
                  <div className="ear left"></div>
                  <div className="ear right"></div>
                </div>
                <div className="pet-face">
                  <div className="pet-eyes">
                    <div className="eye left"></div>
                    <div className="eye right"></div>
                  </div>
                  <div className="pet-nose"></div>
                  <div className="pet-mouth"></div>
                </div>
              </div>
              
              {/* 宠物身体 */}
              <div className="pet-body">
                <div className="pet-chest"></div>
                <div className="pet-belly"></div>
              </div>
              
              {/* 宠物四肢 */}
              <div className="pet-limbs">
                <div className="front-legs">
                  <div className="leg front-left"></div>
                  <div className="leg front-right"></div>
                </div>
                <div className="back-legs">
                  <div className="leg back-left"></div>
                  <div className="leg back-right"></div>
                </div>
              </div>
              
              {/* 宠物尾巴 */}
              <div className="pet-tail"></div>
            </div>
            <div className="pet-accessories">
              {pet.accessories.map(acc => (
                <span key={acc} className={`accessory ${acc}`}>✨</span>
              ))}
            </div>
          </div>
          
          <div className="pet-info">
            <h3>{pet.name} ({petTypes[petType as keyof typeof petTypes].name})</h3>
            <p>等级: {calculateLevel(pet.experience)}</p>
            <p>经验值: {pet.experience}</p>
            <p className="pet-mood">心情: {petMood === 'happy' ? '😊 开心' : petMood === 'excited' ? '🤩 兴奋' : '😋 满足'}</p>
          </div>
        </div>

        <div className="pet-stats">
          <div className="stat-bar">
            <span>快乐值</span>
            <div className="progress-bar">
              <div 
                className="progress happiness" 
                style={{ width: `${pet.happiness}%` }}
              ></div>
            </div>
            <span>{pet.happiness}/100</span>
          </div>
          <div className="stat-bar">
            <span>健康值</span>
            <div className="progress-bar">
              <div 
                className="progress health" 
                style={{ width: `${pet.health}%` }}
              ></div>
            </div>
            <span>{pet.health}/100</span>
          </div>
          <div className="stat-bar">
            <span>饱食度</span>
            <div className="progress-bar">
              <div 
                className="progress hunger" 
                style={{ width: `${pet.hunger}%` }}
              ></div>
            </div>
            <span>{pet.hunger}/100</span>
          </div>
        </div>

        <div className="pet-actions">
          <button className="action-btn feed" onClick={feedPet}>
            🍎 喂食
          </button>
          <button className="action-btn play" onClick={playWithPet}>
            🎾 玩耍
          </button>
          <button className="action-btn chat" onClick={updateChatTime}>
            💬 聊天
          </button>
        </div>
      </div>

      <div className="daily-stats">
        <div className="stat-card">
          <h4>📊 今日数据</h4>
          <p>签到天数: {checkIn.totalDays}</p>
          <p>连续签到: {checkIn.consecutiveDays} 天</p>
          <p>聊天时长: {chatStats.todayMinutes} 分钟</p>
        </div>

        <div className="daily-topic-card">
          <h4>💡 今日话题</h4>
          <p>{dailyTopic}</p>
          <button className="refresh-topic" onClick={generateDailyTopic}>
            🔄 换个话题
          </button>
        </div>
      </div>

      <div className="accessories-shop">
        <h4>🛍️ 装扮商店</h4>
        <div className="accessories-grid">
          {availableAccessories.map(accessory => (
            <div key={accessory.id} className="accessory-item">
              <div className="accessory-icon">✨</div>
              <p>{accessory.name}</p>
              <p className="cost">{accessory.cost} 经验值</p>
              {accessory.unlocked ? (
                <span className="unlocked">已解锁</span>
              ) : (
                <button 
                  className="unlock-btn"
                  onClick={() => unlockAccessory(accessory.id)}
                  disabled={pet.experience < accessory.cost}
                >
                  解锁
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VirtualPet;